// The step timeline (app.md §7), shown in the modal dialog behind the
// header's Pipeline button. Renders the workflow as clickable nodes in
// main-path order (Import Control → Primary → Secondary), then a fork "--<"
// after Secondary Approval (v1.31): the top branch continues the main path
// (Export Control → Closing Confirmation, with Denial Review joined by a
// *dashed* connector so it reads as off the main path), the bottom branch is
// Manual Entry (Process C — dashed stub, a parallel path). Each node shows a
// waiting-count badge. Only nodes the user can act on are lit (in the accent
// color) — the timeline doubles as the role indicator. The selected node is
// filled accent. (A red stale-work dot on the node was tried and removed
// 2026-06-09 — not intuitive; aging lives in the queue rows.)
import { Fragment } from 'react'
import { makeStyles, mergeClasses, tokens, Caption1 } from '@fluentui/react-components'
import { ArchiveRegular } from '@fluentui/react-icons'
import { TIMELINE_NODES, type NodeKey, type Role, type TimelineNode } from '../lib/constants'

// The fork glyph and row centering depend on these fixed sizes: a node is
// NODE_H tall with its circle's center CIRCLE_C from the top; the two branch
// rows stack with BRANCH_GAP between them.
const NODE_H = 70
const CIRCLE_C = 24
const BRANCH_GAP = 8
const FORK_W = 28
const FORK_H = NODE_H * 2 + BRANCH_GAP

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '4px' },
  chain: { display: 'flex', alignItems: 'flex-start' },
  // The fork layout centers the left segment against the two branch rows.
  chainCentered: { display: 'flex', alignItems: 'center' },
  branches: { display: 'flex', flexDirection: 'column', gap: `${BRANCH_GAP}px`, flexGrow: 1 },
  node: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    width: '120px',
    height: `${NODE_H}px`,
    background: 'none',
    border: 'none',
    padding: '4px 0',
    fontFamily: 'inherit',
  },
  circle: {
    position: 'relative',
    width: '40px',
    height: '40px',
    // Keep it a circle: the node is a fixed-height flex column, so a two-line
    // label (e.g. "Primary Approval") would otherwise let flex compress the
    // circle vertically into an oval. flexShrink:0 pins it at 40×40; a long
    // label uses the space below instead.
    flexShrink: 0,
    boxSizing: 'border-box',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  lit: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
  },
  selected: {
    backgroundColor: tokens.colorBrandBackground,
    border: `2px solid ${tokens.colorBrandBackground}`,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  muted: { opacity: 0.5, cursor: 'default' },
  label: { textAlign: 'center', lineHeight: 1.1 },
  labelMuted: { color: tokens.colorNeutralForeground3 },
  // Waiting count rendered inside the node circle.
  count: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, lineHeight: 1 },
  dot: { fontSize: tokens.fontSizeBase300, lineHeight: 1 },
  // Archives node shows a glyph, not a count — its queue is unbounded.
  glyph: { fontSize: '20px', display: 'flex' },
  connector: {
    height: '2px',
    flex: 1,
    minWidth: '16px',
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: `${CIRCLE_C - 1}px`,
  },
  // Branch link to Denial Review: same footprint, dashed instead of solid.
  connectorDashed: {
    height: '0px',
    flex: 1,
    minWidth: '24px',
    borderTop: `2px dashed ${tokens.colorNeutralStroke2}`,
    marginTop: `${CIRCLE_C}px`,
  },
  forkGlyph: { flexShrink: 0, color: tokens.colorNeutralStroke2 },
  // Separates the branch destinations (Denial Review) from the flow proper —
  // a deny can arrive from any approval step, so no single connector is
  // honest. Vertically spans most of the fork block (v1.31 user note).
  vDivider: {
    width: '2px',
    height: `${FORK_H - 24}px`,
    backgroundColor: tokens.colorNeutralStroke2,
    borderRadius: '1px',
    flexShrink: 0,
    margin: '0 20px',
  },
})

interface NodeProps {
  label: string
  count: number
  lit: boolean
  selected: boolean
  onSelect: () => void
  /** Archive node: show a glyph instead of a waiting count. */
  archive?: boolean
}

function Node({ label, count, lit, selected, onSelect, archive }: NodeProps) {
  const s = useStyles()
  const circleClass = mergeClasses(s.circle, lit ? s.lit : s.muted, selected && s.selected)
  return (
    <button
      type="button"
      className={s.node}
      onClick={lit ? onSelect : undefined}
      disabled={!lit}
      aria-pressed={selected}
    >
      <div className={circleClass}>
        {archive ? (
          <ArchiveRegular className={s.glyph} />
        ) : lit ? (
          <span className={s.count}>{count}</span>
        ) : (
          <span className={s.dot}>●</span>
        )}
      </div>
      <Caption1 className={mergeClasses(s.label, !lit && s.labelMuted)}>{label}</Caption1>
    </button>
  )
}

// "--<": a short line in from the fork's anchor node, a vertical spine
// between the two branch rows' circle centers, a solid stub into the top
// (main-path) branch and a dashed stub into the bottom (parallel) branch.
function ForkGlyph() {
  const s = useStyles()
  const mid = FORK_H / 2 - NODE_H / 2 + CIRCLE_C // anchor circle's center
  const top = CIRCLE_C
  const bottom = NODE_H + BRANCH_GAP + CIRCLE_C
  const x = FORK_W / 2
  return (
    <svg
      className={s.forkGlyph}
      width={FORK_W}
      height={FORK_H}
      viewBox={`0 0 ${FORK_W} ${FORK_H}`}
      aria-hidden="true"
    >
      <path d={`M0 ${mid} H${x}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${top} V${bottom}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${top} H${FORK_W}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${bottom} H${FORK_W}`} stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" fill="none" />
    </svg>
  )
}

interface Props {
  litRoles: Set<Role>
  selected: NodeKey | 'admin' | 'dashboard' | null
  counts: Record<string, number>
  onSelect: (key: NodeKey) => void
}

export function Timeline({ litRoles, selected, counts, onSelect }: Props) {
  const s = useStyles()
  // Main path first, then any branch node (Denial Review), then the archive
  // node (rendered separately, past its own divider).
  const main = TIMELINE_NODES.filter((n) => !n.branch && !n.forkFrom && !n.archive)
  const branch = TIMELINE_NODES.filter((n) => n.branch)
  const fork = TIMELINE_NODES.find((n) => n.forkFrom)
  const archive = TIMELINE_NODES.find((n) => n.archive)
  const row = [...main, ...branch]

  const renderNode = (n: TimelineNode) => (
    <Node
      label={n.label}
      count={counts[n.key] ?? 0}
      lit={litRoles.has(n.role)}
      selected={selected === n.key}
      onSelect={() => onSelect(n.key)}
      archive={n.archive}
    />
  )

  // Denial Review + Archives both hang off the flow proper, each past a
  // vertical divider — a deny arrives from any approval step, and Archives is a
  // terminal lookup, not a sequential step.
  const renderTail = () => (
    <>
      {branch.length > 0 && <div className={s.vDivider} aria-hidden />}
      {branch.map((n) => (
        <Fragment key={n.key}>{renderNode(n)}</Fragment>
      ))}
      {archive && <div className={s.vDivider} aria-hidden />}
      {archive && renderNode(archive)}
    </>
  )

  const renderRow = (nodes: TimelineNode[]) => (
    <div className={s.chain}>
      {nodes.map((n, i) => {
        const isLast = i === nodes.length - 1
        // The connector leading into a branch node is dashed.
        const nextDashed = !isLast && nodes[i + 1].branch
        return (
          <Fragment key={n.key}>
            {renderNode(n)}
            {!isLast && <div className={nextDashed ? s.connectorDashed : s.connector} />}
          </Fragment>
        )
      })}
    </div>
  )

  if (!fork)
    return (
      <div className={s.root}>
        <div className={s.chain}>
          {renderRow(main)}
          {renderTail()}
        </div>
      </div>
    )

  // Fork layout: the chain splits after forkFrom — top branch continues the
  // main path, bottom branch is the fork node. Branch destinations (Denial
  // Review) sit past a vertical divider, vertically centered like the left
  // segment — a deny can come from any approval step, so they hang off no
  // particular node.
  const anchorIdx = row.findIndex((n) => n.key === fork.forkFrom)
  const left = row.slice(0, anchorIdx + 1)
  const rightTop = row.slice(anchorIdx + 1).filter((n) => !n.branch)

  return (
    <div className={s.root}>
      <div className={s.chainCentered}>
        {left.map((n, i) => (
          <Fragment key={n.key}>
            {renderNode(n)}
            {i < left.length - 1 && (
              // The centered row is FORK_H tall; pin the connector to the
              // left nodes' circle-center height rather than the row middle.
              <div
                className={s.connector}
                style={{ alignSelf: 'flex-start', marginTop: (FORK_H - NODE_H) / 2 + CIRCLE_C - 1 }}
              />
            )}
          </Fragment>
        ))}
        <ForkGlyph />
        <div className={s.branches}>
          {renderRow(rightTop)}
          {renderRow([fork])}
        </div>
        {renderTail()}
      </div>
    </div>
  )
}
